'use client';

import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import styles from './authFormInput.module.scss';
import { useAuthFormInput } from './useAuthFormInput';

type Props = Omit<ComponentPropsWithoutRef<'input'>, 'prefix'> & {
  error?: boolean;
  errorText?: string;
  disableFloatingLabel?: boolean;
  disableIcon?: boolean;
  icon?: ReactNode;
  trailing?: ReactNode;
};

export function AuthFormInput({
  className,
  id,
  name,
  type = 'text',
  placeholder,
  value,
  error,
  errorText,
  disableFloatingLabel,
  disableIcon,
  icon: iconOverride,
  trailing,
  disabled,
  readOnly,
  onFocus,
  onBlur,
  ...rest
}: Props) {
  const {
    inputId,
    errorId,
    icon,
    showFloatingLabel,
    isFocused,
    hasValue,
    handleFocus,
    handleBlur
  } = useAuthFormInput({
    id,
    name,
    placeholder,
    value,
    type,
    disableFloatingLabel,
    disableIcon,
    icon: iconOverride,
    errorText,
    onFocus,
    onBlur
  });

  const containerClassName = [
    styles.root,
    error ? styles.error : undefined,
    disabled || readOnly ? styles.disabled : undefined,
    isFocused || hasValue ? styles.focused : undefined,
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClassName}>
      <div className={styles.inputRow}>
        {icon ? <div className={styles.icon}>{icon}</div> : null}

        {showFloatingLabel ? (
          <label
            htmlFor={inputId}
            className={[
              styles.floatingLabel,
              icon ? styles.floatingLabelWithIcon : undefined
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {placeholder}
          </label>
        ) : null}

        <input
          {...rest}
          id={inputId}
          name={name}
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          className={styles.input}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {trailing ? <div className={styles.trailing}>{trailing}</div> : null}
      </div>

      {error && errorText ? (
        <div id={errorId} className={styles.errorText}>
          {errorText}
        </div>
      ) : null}
    </div>
  );
}
